// OBT F3-E — Bildirim listesi + okunmamış sayacı (poll'lü, salt-okunur güvenli).
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchObtNotifications,
  markObtNotificationRead,
  markAllObtNotificationsRead,
} from '../notificationApi';
import { normalizeObtError } from '../../obtErrors';

function isCanceled(error) {
  return (
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError'
  );
}

const POLL_MS = 60_000;

export function useObtNotifications({ enabled = true } = {}) {
  const [state, setState] = useState({
    loading: enabled,
    error: null,
    items: [],
    unreadCount: 0,
  });
  const abortRef = useRef(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    if (!enabled) {
      setState({ loading: false, error: null, items: [], unreadCount: 0 });
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState((prev) => ({ ...prev, loading: prev.items.length === 0, error: null }));
    try {
      const data = await fetchObtNotifications({ limit: 30 }, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setState({
        loading: false,
        error: null,
        items: Array.isArray(data?.items) ? data.items : [],
        unreadCount: data?.unread_count ?? 0,
      });
    } catch (error) {
      if (isCanceled(error) || controller.signal.aborted) return;
      setState((prev) => ({ ...prev, loading: false, error: normalizeObtError(error) }));
    }
  }, [enabled]);

  useEffect(() => {
    load();
    if (enabled) {
      timerRef.current = setInterval(load, POLL_MS);
    }
    return () => {
      abortRef.current?.abort();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [load, enabled]);

  const markRead = useCallback(
    async (id) => {
      try {
        await markObtNotificationRead(id);
      } catch {
        /* okundu işaretleme başarısızsa sessiz; liste yenilenince düzelir */
      }
      await load();
    },
    [load]
  );

  const markAll = useCallback(async () => {
    try {
      await markAllObtNotificationsRead();
    } catch {
      /* sessiz */
    }
    await load();
  }, [load]);

  return { ...state, reload: load, markRead, markAll };
}
